/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
import { useApolloClient } from "@apollo/client";
import FundamenteiOutline from "components/branding/FundamenteiOutline";
import Avatar from "components/core/Avatar";
import DarkModeButtonV2 from "components/financials/modern/DarkModeButtonV2";
import SelectExportFormat from "components/financials/modern/SelectExportFormat";
import SelectPlatformVertical from "components/financials/modern/SelectPlatformVertical";
import SelectTickerSymbolList from "components/financials/modern/SelectTickerSymbolList";
import type { ScreenerGridProps } from "components/screener/ScreenerGrid";
import PaginationControls, {
  paginationStateAtom,
  usePaginationInputControls,
} from "components/screener/v2/PaginationControls";
import { PrimarySymbolToggle } from "components/screener/v2/PrimarySymbolToggle";
import { RunScreenerInput } from "components/screener/v2/RunScreenerInput";
import { SaveScreener } from "components/screener/v2/SaveScreener";
import { SavedScreeners } from "components/screener/v2/SavedScreeners";
import ScreenerExpressCheckout from "components/screener/v2/ScreenerExpressCheckout";
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import { ScreenerFilterableIndicators, SelectDebouncer } from "components/screener/v2/ScreenerFilterableIndicators";
import { ScreenerGridEmitter } from "components/screener/v2/ScreenerGridEmitter";
import { getGraphQLError, getNetworkError, isApolloServerError } from "components/screener/v2/apollo";
import { AtomValue, runScreenerQueryStateAtom } from "components/screener/v2/atoms";
import { FiltersBar } from "components/screener/v2/filters/FiltersBar";
import { openFilterPopover } from "components/screener/v2/filters/focus";
import { useFilters } from "components/screener/v2/filters/useFilters";
import type { IRunScreenerInputQuery } from "components/screener/v2/query/__generated__/RunScreenerInputQuery.generated";
import type { IScreenerDefaultsCombinedQuery } from "components/screener/v2/query/__generated__/ScreenerDefaultsCombinedQuery.generated";
import FreeMode from "components/screener/v2/tags/FreeMode";
import type {
  ExactPlatformVerticals,
  ScreenerURLState,
  SetScreenerURLState,
} from "components/screener/v2/urlSearchParams";
import { deriveUrlStateFromInput, makeEmptyScreenerUrlState } from "components/screener/v2/urlSearchParams";
import TwoPassRender from "components/shared/TwoPassRender";
import FeedbackDialog from "components/support/FeedbackDialog";
import { Button } from "components/ui/Button";
import { Spinner } from "components/ui/Spinner";
import classNames from "components/ui/classNames";
import aspectRatio from "components/utils/aspectRatio";
import secondsToHours from "date-fns/secondsToHours";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { FundamentalProductOriginCodes } from "lib/analytics/FundamenteiProductCodes";
import trackEvent from "lib/analytics/trackEvent";
import { getFlagValue } from "lib/flagging/FeatureFlags";
import useSelfie from "lib/hooks/useSelfie";
import delayedPromise from "lib/utils/delayedPromise";
import debounce from "lodash/debounce";
import get from "lodash/get";
import { ChevronDownIcon, GaugeIcon, HomeIcon, RefreshCwIcon, Undo2Icon } from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import type { ReactNode } from "react";
import { startTransition, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import {
  IFinancialsStatementType,
  IIndicatorDataSource,
  IPlatformVertical,
  IScreenerFilterConditionsBinaryOperator,
} from "types/graphqlTypes";
import { useMediaQuery } from "usehooks-ts";

const DynamicScreenerGrid = dynamic<ScreenerGridProps>(() => import("../ScreenerGrid"), {
  loading: () => <FullHeightLoadingBlankslate />,
  ssr: true,
});

interface IsolatedScreenerProps {
  language: "pt-BR" | "en-US";
  runScreenerInput?: NonNullable<IRunScreenerInputQuery["runScreenerInput"]> | null;
  state: ScreenerURLState;
  setURLState: SetScreenerURLState;
  screenerDefaults?: IScreenerDefaultsCombinedQuery["screenerDefaults"] | null;
}

export default function IsolatedScreener({
  state,
  setURLState,
  language,
  runScreenerInput = null,
  screenerDefaults = null,
}: IsolatedScreenerProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const amplitude = useAmplitude();

  const apolloClient = useApolloClient();

  const {
    platformVertical,
    primaryDataSource,
    statementType,
    isPrimaryTickerSymbol,
    storedRunInputId,
    filterByTickerSymbolList,
    sortSpecifier,
  } = state;

  const defaultSelectedIndicators = screenerDefaults?.selectedIndicators.find((candidate) => {
    return candidate.vertical === platformVertical && candidate.primaryDataSource === primaryDataSource;
  })?.indicators;
  const [selectedIndicators, setSelectedIndicators] = useState<FilterableIndicator[]>(
    runScreenerInput?.selectedIndicators || defaultSelectedIndicators || [],
  );

  const {
    activeFilters,
    filterConditions,
    filterConditionsBinaryOperator,

    toggleActiveFilter,
    setActiveFilters,
    setFilterConditions,
    setFiltersPipelineBinaryOperator,
    loadFiltersFromInput,
    resetAllFilters,
  } = useFilters(runScreenerInput, selectedIndicators);

  const safePrimaryDataSource = inferPrimaryDataSource(platformVertical);
  const paginationInputControls = usePaginationInputControls();
  // Read from existing financials table settings
  const textAlign = ["left", "center", "right"].includes(selfie?.settingsV2.financialsTable?.textAlignment || "")
    ? (selfie?.settingsV2.financialsTable?.textAlignment as "left" | "center" | "right")
    : "left";
  const cannotInteract = !selfie?.isPremium;
  const isSignedOut = selfie === null || selfie === undefined;
  const isDesktopWidth = useMediaQuery("(min-width: 1024px)");
  const IS_DEBUGGING = false;
  const filterableIndicators = selectedIndicators.filter((node) => node.viewerCanFilter);

  return (
    <div className="flex h-full w-full flex-col gap-y-2 lg:gap-y-4">
      {!isDesktopWidth && IS_DEBUGGING && (
        <TwoPassRender>
          {() => {
            return (
              <div className="break-all rounded-xl bg-slate-600/10 p-4 font-mono45 text-xs dark:bg-slate-900 dark:text-yellow-300">
                {[window.location.pathname, window.location.search].join("")}
              </div>
            );
          }}
        </TwoPassRender>
      )}
      <TwoPassRender>
        {() => {
          const screenerFilterableIndicators = (
            <SelectDebouncer
              selectedIndicators={selectedIndicators}
              onCommit={(indicators) => {
                setSelectedIndicators(indicators);

                amplitude.logEvent("Selected screener indicators", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  vertical: platformVertical,
                  primaryDataSource,
                  tickerSymbolList: filterByTickerSymbolList || "ALL",
                  indicators: indicators.map((node) => node.name),
                });
                trackEvent("Selected screener indicators", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  vertical: platformVertical,
                  primaryDataSource,
                  tickerSymbolList: filterByTickerSymbolList || "ALL",
                  indicators: indicators.map((node) => node.name),
                });
              }}
            >
              {({
                pendingCount,
                removedCount,
                selectedIndicators,
                onSelectIndicator,
                onRemoveIndicator,
                onRemoveAll,
              }) => (
                <ScreenerFilterableIndicators
                  language={language}
                  vertical={platformVertical}
                  statementType={statementType}
                  pendingCount={pendingCount}
                  removedCount={removedCount}
                  primaryDataSource={primaryDataSource}
                  selectedIndicators={selectedIndicators}
                  onSelect={(indicator, event) => {
                    onSelectIndicator(indicator, event);
                    if (
                      getFlagValue("features/screener")?.toggleActiveFilterOnSelectIndicator &&
                      indicator.viewerCanFilter
                    ) {
                      toggleActiveFilter(indicator);
                    }
                  }}
                  onRemove={onRemoveIndicator}
                  onRemoveAll={onRemoveAll}
                />
              )}
            </SelectDebouncer>
          );
          const selectPlatformVertical = (
            <SelectPlatformVertical
              value={platformVertical}
              onChange={(vertical) => {
                const defaultSelectedIndicators =
                  screenerDefaults?.selectedIndicators.find((candidate) => candidate.vertical === vertical)
                    ?.indicators || [];

                amplitude.logEvent("Changed screener vertical", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  vertical,
                  tickerSymbolList: filterByTickerSymbolList || "ALL",
                });
                trackEvent("Changed screener vertical", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  vertical,
                  tickerSymbolList: filterByTickerSymbolList || "ALL",
                });

                if (vertical === IPlatformVertical.Stocks || vertical === IPlatformVertical.Reits) {
                  setSelectedIndicators(defaultSelectedIndicators);
                  setURLState({
                    vertical,
                    primaryDataSource: null,
                    statementType: IFinancialsStatementType.Annual,
                    isPrimaryTickerSymbol: true,
                  });
                }
                if (vertical === IPlatformVertical.Fiis) {
                  setSelectedIndicators(defaultSelectedIndicators);
                  setURLState({
                    vertical,
                    primaryDataSource: IIndicatorDataSource.Fnet,
                    statementType: IFinancialsStatementType.Monthly,
                    isPrimaryTickerSymbol: true,
                  });
                }
                if (vertical === IPlatformVertical.Acoes) {
                  setSelectedIndicators(defaultSelectedIndicators);
                  setURLState({
                    vertical,
                    isPrimaryTickerSymbol: true,
                    primaryDataSource: IIndicatorDataSource.Cvm,
                    statementType: IFinancialsStatementType.Annual,
                  });
                }
                paginationInputControls.resetPagination();
              }}
            />
          );
          const selectTickerSymbolList = (
            <SelectTickerSymbolList
              disabled={cannotInteract}
              value={filterByTickerSymbolList || null}
              onChange={(list) => {
                setURLState({ list });
                paginationInputControls.resetPagination();

                amplitude.logEvent("Changed screener symbol list", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  tickerSymbolList: list || "ALL",
                });
                trackEvent("Changed screener symbol list", {
                  origin: FundamentalProductOriginCodes.SCREENER,
                  tickerSymbolList: list || "ALL",
                });
              }}
            />
          );
          return (
            <>
              <div className="flex flex-col items-center justify-between lg:flex-row landscape:max-sm:hidden">
                {/* Mobile */}
                <Toggle initial={false}>
                  {({ toggle, on }) => {
                    return (
                      <>
                        {!isDesktopWidth && (
                          <div className="flex w-full items-center gap-x-4 gap-y-2 overflow-x-scroll py-1 lg:hidden lg:py-0">
                            {screenerFilterableIndicators}
                            {selectPlatformVertical}

                            <Link
                              href="/"
                              className={classNames(
                                "flex size-9 min-w-9 items-center justify-center rounded-full lg:hidden",
                                "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                "border border-slate-300 outline-none",
                                "dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 hover:dark:text-blue-300",
                                "focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                                "dark:hover:ring-slate-500 dark:hover:ring-offset-slate-500",
                              )}
                            >
                              <HomeIcon className="size-5" />
                            </Link>

                            <AtomValue atom={paginationStateAtom}>
                              {(state) => (
                                <button
                                  disabled={state?.loading}
                                  type="button"
                                  onClick={toggle}
                                  className={classNames(
                                    "flex size-9 min-w-9 items-center justify-center rounded-full",
                                    "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300",
                                    "border border-slate-300 hover:bg-gray-200 hover:text-gray-900 dark:border-slate-600 hover:dark:text-yellow-300",
                                    "outline-none",
                                    "focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                                    "dark:hover:ring-slate-500 dark:hover:ring-offset-slate-500",
                                  )}
                                >
                                  {state?.loading ? (
                                    <Spinner strokeWidth={2} />
                                  ) : (
                                    <ChevronDownIcon
                                      className={classNames("transform-gpu transition-transform", {
                                        "rotate-180": on,
                                      })}
                                    />
                                  )}
                                </button>
                              )}
                            </AtomValue>
                          </div>
                        )}

                        {on && (
                          <div className="mt-2.5 flex w-full flex-wrap gap-2.5 rounded-2xl bg-sea-300/10 px-2 py-2">
                            <div className="flex w-full flex-wrap gap-2.5">
                              {selectTickerSymbolList}
                              {platformVertical === IPlatformVertical.Acoes && (
                                <PrimarySymbolToggle
                                  disabled={isSignedOut}
                                  isPrimaryTickerSymbol={isPrimaryTickerSymbol === true}
                                  onChange={(isPrimaryTickerSymbol) => setURLState({ isPrimaryTickerSymbol })}
                                />
                              )}
                            </div>

                            <div className="flex w-full flex-wrap gap-2.5">
                              <SavedScreeners
                                key={`savedScreeners/${storedRunInputId}`}
                                disabled={cannotInteract}
                                language={language}
                                activeObjectId={storedRunInputId}
                                onSelect={(rsi) => {
                                  RunScreenerInput.resetOriginalInput();
                                  if (rsi) {
                                    startTransition(() => {
                                      setURLState(deriveUrlStateFromInput(rsi));
                                      setSelectedIndicators(rsi.selectedIndicators);
                                      loadFiltersFromInput(rsi);
                                      if (rsi.objectId !== storedRunInputId) {
                                        paginationInputControls.resetPagination();
                                      }
                                    });
                                  } else {
                                    startTransition(() => {
                                      paginationInputControls.resetPagination();
                                      setURLState(
                                        makeEmptyScreenerUrlState(platformVertical as ExactPlatformVerticals),
                                      );
                                      setSelectedIndicators([]);
                                      resetAllFilters();
                                    });
                                  }
                                }}
                              />

                              {(runScreenerInput?.viewerCanEdit || !storedRunInputId) && (
                                <SaveScreener
                                  key={`saveScreener/${runScreenerInput?.objectId || "default"}`}
                                  disabled={cannotInteract}
                                  objectId={storedRunInputId}
                                  runScreenerInput={runScreenerInput}
                                  onSave={({ objectId }) => setURLState({ input: objectId })}
                                />
                              )}
                            </div>

                            <div className="flex w-full flex-wrap gap-2.5">
                              <AtomValue atom={paginationStateAtom}>
                                {(paginationState) => (
                                  <PaginationControls
                                    disabled={cannotInteract}
                                    onChange={(params) => paginationInputControls.skipOffset(params.skipOffset)}
                                    state={{
                                      totalRows: 0,
                                      skipOffset: 0,
                                      rowsInGrid: 0,
                                      currentLimit: 30,
                                      hasNextPage: false,
                                      ...paginationState,
                                    }}
                                  />
                                )}
                              </AtomValue>

                              <button
                                type="button"
                                onClick={() => {
                                  startTransition(() => {
                                    RunScreenerInput.pushGridKeyInvalidation();
                                    paginationInputControls.resetPagination();
                                    setURLState(makeEmptyScreenerUrlState());
                                    setSelectedIndicators(defaultSelectedIndicators || []);
                                    resetAllFilters();
                                  });
                                }}
                                className={classNames(
                                  "relative flex size-10 items-center justify-center rounded-full border border-slate-300 bg-white p-2",
                                  "hover:bg-slate-100",
                                  "aria-checked:cursor-pointer aria-checked:bg-slate-200",
                                  "group",
                                  "hover:border-blue-400 hover:bg-blue-50",
                                  "focus:outline-none",
                                  "disabled:pointer-events-none disabled:opacity-50",
                                  "dark:border-slate-950 dark:bg-slate-700",
                                  "dark:hover:bg-lime-400/30",
                                )}
                              >
                                <Undo2Icon className="size-6" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }}
                </Toggle>

                {isDesktopWidth && (
                  <div className="hidden w-full flex-wrap items-center gap-x-4 gap-y-2 lg:flex">
                    {screenerFilterableIndicators}
                    {selectPlatformVertical}

                    {(platformVertical === IPlatformVertical.Acoes ||
                      platformVertical === IPlatformVertical.Stocks) && (
                      <PrimarySymbolToggle
                        disabled={isSignedOut}
                        isPrimaryTickerSymbol={isPrimaryTickerSymbol === true}
                        onChange={(isPrimaryTickerSymbol) => setURLState({ isPrimaryTickerSymbol })}
                      />
                    )}

                    {selectTickerSymbolList}
                    <SavedScreeners
                      key={`savedScreeners/${storedRunInputId}`}
                      disabled={cannotInteract}
                      language={language}
                      activeObjectId={storedRunInputId}
                      onSelect={(rsi) => {
                        if (rsi) {
                          startTransition(() => {
                            setURLState(deriveUrlStateFromInput(rsi));
                            setSelectedIndicators(rsi.selectedIndicators);
                            loadFiltersFromInput(rsi);
                            if (rsi.objectId !== storedRunInputId) {
                              paginationInputControls.resetPagination();
                            }
                          });
                        } else {
                          startTransition(() => {
                            paginationInputControls.resetPagination();
                            setURLState(
                              makeEmptyScreenerUrlState(
                                platformVertical as ExactPlatformVerticals,
                                platformVertical === IPlatformVertical.Acoes ? safePrimaryDataSource : null,
                              ),
                            );
                            setSelectedIndicators([]);
                            resetAllFilters();
                          });
                        }
                      }}
                    />

                    {(runScreenerInput?.viewerCanEdit || !storedRunInputId) && (
                      <SaveScreener
                        key={`saveScreener/${runScreenerInput?.objectId || "default"}`}
                        disabled={cannotInteract}
                        objectId={storedRunInputId}
                        runScreenerInput={runScreenerInput}
                        onSave={({ objectId }) => setURLState({ input: objectId })}
                      />
                    )}

                    <AtomValue atom={paginationStateAtom}>
                      {(paginationState) => (
                        <PaginationControls
                          disabled={cannotInteract}
                          onChange={(params) => paginationInputControls.skipOffset(params.skipOffset)}
                          state={{
                            totalRows: 0,
                            skipOffset: 0,
                            rowsInGrid: 0,
                            currentLimit: 30,
                            hasNextPage: false,
                            ...paginationState,
                          }}
                        />
                      )}
                    </AtomValue>

                    {selfie?.viewerCanExportFinancialsData && isDesktopWidth && (
                      <SelectExportFormat
                        onSelectExportFormat={(format) => ScreenerGridEmitter.emit("exportAs", format)}
                      />
                    )}

                    {!selfie?.isPremium && <FreeMode />}

                    <AtomValue atom={paginationStateAtom}>
                      {(state) => (state?.loading ? <Spinner strokeWidth={2} /> : null)}
                    </AtomValue>
                  </div>
                )}

                {isDesktopWidth && (
                  <div className="hidden items-center justify-end gap-x-4 text-right empty:hidden lg:flex">
                    {selfie?.isPremium && getFlagValue("features/screener")?.enableSubmitFeedbackButton && (
                      <FeedbackDialog
                        title="Screener"
                        description={
                          <FormattedMessage
                            defaultMessage="Nos ajude a melhorar o planilhão. Compartilhe seu feedback."
                            id="YMIYwV"
                          />
                        }
                      >
                        <Button shape="capsule" variant="primary/teal">
                          <FormattedMessage defaultMessage="Feedback" id="Ejhdi4" />
                        </Button>
                      </FeedbackDialog>
                    )}

                    <button
                      type="button"
                      title={intl.formatMessage({
                        defaultMessage: "Reiniciar o screener",
                        id: "3H6bhZ",
                      })}
                      onClick={async () => {
                        amplitude.logEvent("Clicked on reset screener", {
                          origin: FundamentalProductOriginCodes.SCREENER,
                        });
                        trackEvent("Clicked on reset screener", {
                          origin: FundamentalProductOriginCodes.SCREENER,
                        });

                        await toast.promise(
                          delayedPromise(400).then(() => {
                            window.location.href = "/screener";
                          }),
                          {
                            loading: intl.formatMessage({ defaultMessage: "Reiniciando...", id: "54NkTY" }),
                            success: intl.formatMessage({ defaultMessage: "Reiniciando...", id: "54NkTY" }),
                            error: intl.formatMessage({ defaultMessage: "Reiniciando...", id: "54NkTY" }),
                          },
                        );
                      }}
                      className={classNames(
                        "hidden size-10 items-center justify-center rounded-full lg:flex",
                        "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                        "border border-slate-300 outline-none",
                        "dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 hover:dark:text-blue-300",
                        "focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                        "dark:hover:ring-slate-500 dark:hover:ring-offset-slate-500",
                      )}
                    >
                      <RefreshCwIcon />
                    </button>

                    <DarkModeButtonV2 persistency={selfie ? "userSettings" : "queryString"} />

                    {selfie && (
                      <Link
                        href="/"
                        className={classNames(
                          "flex items-center justify-center rounded-full ring-slate-600/20 ring-offset-2",
                          "hover:ring-[1.5px]",
                          "dark:ring-slate-600/40 dark:ring-offset-slate-800",
                        )}
                      >
                        <Avatar
                          variant="rounded"
                          className="border border-slate-300 dark:border-slate-800"
                          src={selfie.pictureUrl}
                          hashValue={selfie._id}
                          alt={selfie.fullName}
                          size={40}
                        />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </>
          );
        }}
      </TwoPassRender>

      <FiltersBar
        disabled={cannotInteract || filterableIndicators.length === 0}
        screenerContext={{
          language,
          isPrimaryTickerSymbol,
          filterByTickerSymbolList,
          vertical: platformVertical,
          filterConditions: {
            conditions: filterConditions,
            operator:
              filterConditionsBinaryOperator === "AND"
                ? IScreenerFilterConditionsBinaryOperator.And
                : IScreenerFilterConditionsBinaryOperator.Or,
          },
        }}
        activeFilters={activeFilters}
        selectedIndicators={filterableIndicators}
        conditions={filterConditions}
        pipelineBinaryOperator={filterConditionsBinaryOperator}
        onChangePipelineBinaryOperator={(binaryOperator) => {
          setFiltersPipelineBinaryOperator(binaryOperator);
          paginationInputControls.resetPagination();
        }}
        onConditionsChange={(filterConditions) => {
          setFilterConditions(filterConditions);
          paginationInputControls.resetPagination();

          const indicators = selectedIndicators.filter(({ objectId }) => {
            return filterConditions.some(({ identifier }) => identifier === objectId);
          });

          amplitude.logEvent("Applied screener filter conditions", {
            origin: FundamentalProductOriginCodes.SCREENER,
            vertical: platformVertical,
            primaryDataSource,
            tickerSymbolList: filterByTickerSymbolList || "ALL",
            indicators: indicators.map((node) => node.name),
          });
          trackEvent("Applied screener filters conditions", {
            origin: FundamentalProductOriginCodes.SCREENER,
            vertical: platformVertical,
            primaryDataSource,
            tickerSymbolList: filterByTickerSymbolList || "ALL",
            indicators: indicators.map((node) => node.name),
          });
        }}
        onSelect={(indicator, isSelected) => {
          toggleActiveFilter(indicator, isSelected);
          paginationInputControls.resetPagination();
        }}
        onSelectMany={(indicators) => {
          setActiveFilters(indicators);
          paginationInputControls.resetPagination();

          amplitude.logEvent("Changed screener filters", {
            origin: FundamentalProductOriginCodes.SCREENER,
            vertical: platformVertical,
            primaryDataSource,
            tickerSymbolList: filterByTickerSymbolList || "ALL",
            indicators: indicators.map((node) => node.name),
          });
          trackEvent("Changed screener filters", {
            origin: FundamentalProductOriginCodes.SCREENER,
            vertical: platformVertical,
            primaryDataSource,
            tickerSymbolList: filterByTickerSymbolList || "ALL",
            indicators: indicators.map((node) => node.name),
          });
        }}
      />

      <AtomValue atom={runScreenerQueryStateAtom}>
        {(value) => {
          const error = getNetworkError(value?.error) || getGraphQLError(value?.error);
          const isRateLimited =
            (error && isApolloServerError(error) && error.response.status === 429) ||
            (error && error.message === "Too Many Requests");
          if (isRateLimited) {
            const rateLimitInfo = get(error, "result.extensions.metadata.rateLimit", {}) as {
              retryAfter?: number;
              limit?: number;
              remaining?: number;
              reset?: string;
            };
            return (
              <FullWidthErrorBanner className="justify-center text-center">
                <div className="flex flex-col items-center">
                  <GaugeIcon className="size-8" />
                  <h1 className="text-base font-bold lg:text-lg">
                    <FormattedMessage
                      defaultMessage="Você esgotou o limite de {limit} usos gratuitos por dia"
                      id="56BEr2"
                      values={{
                        limit: rateLimitInfo.limit,
                      }}
                    />
                  </h1>
                  {rateLimitInfo.retryAfter && (
                    <p className="text-sm font-medium lg:text-base">
                      <FormattedMessage
                        defaultMessage="Seu limite será liberado novamente {retryAfter}"
                        id="uc7gYl"
                        values={{
                          retryAfter: intl.formatRelativeTime(secondsToHours(rateLimitInfo.retryAfter), "hours"),
                        }}
                      />
                    </p>
                  )}
                  <ScreenerExpressCheckout>
                    <button
                      type="button"
                      className="text-sm font-bold text-blue-600 hover:underline hover:underline-offset-4 dark:text-blue-300 lg:text-base"
                    >
                      <FormattedMessage defaultMessage="No Premium, não há limites. Assine já!" id="GlGpBA" />
                    </button>
                  </ScreenerExpressCheckout>
                </div>
              </FullWidthErrorBanner>
            );
          }
          return null;
        }}
      </AtomValue>

      {!getFlagValue("features/isNewScreenerEnabled") ? (
        <FullHeightErrorCanvas>
          <FormattedMessage defaultMessage="O novo screener ainda não está habilitado para você..." id="vnZaLa" />
        </FullHeightErrorCanvas>
      ) : (
        <DynamicScreenerGrid
          key={RunScreenerInput.popGridKeyInvalidation()}
          apolloClient={apolloClient}
          isDesktop={isDesktopWidth}
          textAlign={textAlign}
          vertical={platformVertical}
          primaryDataSource={safePrimaryDataSource}
          statementType={statementType}
          isPrimaryTickerSymbol={isPrimaryTickerSymbol === true}
          filterByTickerSymbolList={filterByTickerSymbolList}
          selectedIndicators={selectedIndicators}
          filterableIndicators={filterableIndicators}
          sortSpecifiers={sortSpecifier ? [sortSpecifier] : []}
          filterConditions={filterConditions}
          filterConditionsBinaryOperator={filterConditionsBinaryOperator}
          onChangeSort={(sortSpecifier) => {
            paginationInputControls.resetPagination();
            setURLState({ sortBy: sortSpecifier });

            const indicator = selectedIndicators.find(({ objectId }) => objectId === sortSpecifier?.identifier);
            if (sortSpecifier?.identifier && indicator) {
              amplitude.logEvent("Sorted a screener column", {
                origin: FundamentalProductOriginCodes.SCREENER,
                vertical: platformVertical,
                primaryDataSource: safePrimaryDataSource,
                tickerSymbolList: filterByTickerSymbolList || "ALL",
                indicator: indicator?.name,
                direction: sortSpecifier.direction,
              });
              trackEvent("Sorted a screener column", {
                origin: FundamentalProductOriginCodes.SCREENER,
                vertical: platformVertical,
                primaryDataSource: safePrimaryDataSource,
                tickerSymbolList: filterByTickerSymbolList || "ALL",
                indicator: indicator?.name,
                direction: sortSpecifier.direction,
              });
            }
          }}
          onRemoveIndicatorColumn={(indicator) => {
            setSelectedIndicators(selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId));
          }}
          onReorderSelectedIndicators={debounce(
            (reorderedSelectedIndicators) => setSelectedIndicators(reorderedSelectedIndicators),
            500,
          )}
          onToggleFilterColumn={(indicator) => {
            toggleActiveFilter(indicator);
            setTimeout(() => {
              openFilterPopover(indicator);
            }, 10);
          }}
        />
      )}
    </div>
  );
}

function inferPrimaryDataSource(vertical: IPlatformVertical) {
  if (vertical === IPlatformVertical.Acoes) {
    return IIndicatorDataSource.Cvm;
  }
  if (vertical === IPlatformVertical.Fiis) {
    return IIndicatorDataSource.Fnet;
  }
  return IIndicatorDataSource.Morningstar;
}

interface FullWidthErrorBannerProps {
  children: ReactNode;
  className?: string;
}
function FullWidthErrorBanner({ children, className, ...props }: FullWidthErrorBannerProps) {
  return (
    <div
      {...props}
      className={classNames(
        "flex",
        "w-full rounded-xl p-4",
        "bg-rose-50 font-medium text-rose-600 ring-1 ring-red-800/20",
        "dark:bg-rose-600/5 dark:font-bold dark:text-rose-200 dark:ring-slate-950",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface FullHeightErrorCanvasProps {
  children: ReactNode;
}
function FullHeightErrorCanvas({ children }: FullHeightErrorCanvasProps) {
  return (
    <div
      className={classNames(
        "flex items-center justify-center text-center",
        "h-full min-h-14 w-full rounded-xl px-8",
        "bg-slate-100 font-medium text-slate-600 ring-1 ring-slate-300 ring-offset-2 ring-offset-white",
        "dark:bg-rose-300/10 dark:font-bold dark:text-rose-200 dark:ring-slate-900 dark:ring-offset-slate-900",
      )}
    >
      {children}
    </div>
  );
}

function FullHeightLoadingBlankslate() {
  return (
    <div
      className={classNames(
        "h-full w-full animate-pulse rounded-xl bg-slate-100 ring-1 ring-slate-300 ring-offset-2 ring-offset-white",
        "dark:bg-slate-800 dark:ring-slate-900 dark:ring-offset-slate-900",
        "flex items-center justify-center",
      )}
    >
      <FundamenteiOutline
        strokeWidth={4}
        {...aspectRatio({
          originalWidth: 263,
          originalHeight: 278,
          desiredHeight: 80,
        })}
      />
    </div>
  );
}
